import { createSelector, createSlice } from '@reduxjs/toolkit';
import { logoutUser } from '@/features/auth';
import type { RootState } from '@/store/root-state';
import { findHomeownerPoolUserBySub, listHomeownerPoolUsers } from './homeownerUsersPoolThunks';
import type { HomeownerPoolErrorKind, HomeownerPoolUser, HomeownerUsersPoolState } from './homeownerUsersPoolTypes';

const initialState: HomeownerUsersPoolState = {
  users: [],
  exactUser: null,
  nextToken: null,
  hasMore: true,
  isLoading: false,
  hasFetched: false,
  error: null,
  errorKind: null,
};

function userKey(user: HomeownerPoolUser): string {
  return user.sub?.trim() || user.username?.trim() || '';
}

function mergeUsers(existing: HomeownerPoolUser[], incoming: HomeownerPoolUser[]): HomeownerPoolUser[] {
  const merged = [...existing];
  incoming.forEach((user) => {
    const key = userKey(user);
    const index = key ? merged.findIndex((candidate) => userKey(candidate) === key) : -1;
    if (index >= 0) merged[index] = user;
    else merged.push(user);
  });
  return merged;
}

function errorKind(value?: string): HomeownerPoolErrorKind {
  if (value === 'invalid-search' || value === 'access-denied' || value === 'not-found') return value;
  return 'unavailable';
}

const homeownerUsersPoolSlice = createSlice({
  name: 'homeownerUsersPool',
  initialState,
  reducers: {
    clearHomeownerUsersPool: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(listHomeownerPoolUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.errorKind = null;
        state.exactUser = null;
      })
      .addCase(listHomeownerPoolUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasFetched = true;
        state.nextToken = action.payload.data.nextToken;
        state.hasMore = action.payload.data.nextToken !== null;
        state.users = action.meta.arg?.nextToken
          ? mergeUsers(state.users, action.payload.data.items)
          : action.payload.data.items;
      })
      .addCase(listHomeownerPoolUsers.rejected, (state, action) => {
        if (action.meta.condition) return;
        state.isLoading = false;
        state.hasFetched = true;
        state.users = [];
        state.nextToken = null;
        state.hasMore = false;
        state.error = action.payload?.message ?? 'Homeowner authentication identities are temporarily unavailable.';
        state.errorKind = errorKind(action.payload?.code);
      })
      .addCase(findHomeownerPoolUserBySub.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.errorKind = null;
        state.exactUser = null;
      })
      .addCase(findHomeownerPoolUserBySub.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasFetched = true;
        state.exactUser = action.payload.data;
      })
      .addCase(findHomeownerPoolUserBySub.rejected, (state, action) => {
        if (action.meta.condition) return;
        state.isLoading = false;
        state.hasFetched = true;
        state.exactUser = null;
        state.error = action.payload?.message ?? 'Homeowner authentication identities are temporarily unavailable.';
        state.errorKind = errorKind(action.payload?.code);
      })
      .addCase(logoutUser.fulfilled, () => ({ ...initialState }));
  },
});

const selectState = (state: RootState) => state.homeownerUsersPool;

export const selectHomeownerPoolUsers = createSelector([selectState], (state) => state.users);
export const selectExactHomeownerPoolUser = createSelector([selectState], (state) => state.exactUser);
export const selectHomeownerPoolNextToken = createSelector([selectState], (state) => state.nextToken);
export const selectHomeownerPoolHasMore = createSelector([selectState], (state) => state.hasMore);
export const selectHomeownerPoolLoading = createSelector([selectState], (state) => state.isLoading);
export const selectHomeownerPoolHasFetched = createSelector([selectState], (state) => state.hasFetched);
export const selectHomeownerPoolError = createSelector([selectState], (state) => state.error);
export const selectHomeownerPoolErrorKind = createSelector([selectState], (state) => state.errorKind);

export const { clearHomeownerUsersPool } = homeownerUsersPoolSlice.actions;
export default homeownerUsersPoolSlice.reducer;
